import React, { useEffect, useState, useMemo } from "react";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaDownload,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
} from "react-icons/fa";
import dayjs from "dayjs";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";
import { useScreenPermissions } from "./useScreenPermissions";
import ProductionOrderFormModal from "./ProductionOrderFormModal";
import ProductionOrderViewModal from "./ProductionOrderViewModal";

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
    />
  </svg>
);

const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c12.5-12.5 12.5-32.8 0-45.3s32.8-12.5-45.3 0l160 160z"
    />
  </svg>
);

const API_URL = "/api";

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to make authenticated API requests
const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};

const ProductionOrderMaster = () => {
  const [productionOrders, setProductionOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("All");
  const [apiError, setApiError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [formData, setFormData] = useState({
    products: [],
    nextOrderNumber: '',
    nextDetailNumber: ''
  });
  
  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  // State to force re-render of modals
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Get user permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  
  // --- THEME HOOKS (must run unconditionally) ---
  // Use slate-800 (#1f2937) as accent so Create New, active sidebar, logout, and profile match.
  const pageTheme = useMemo(() => ({
    bg: '#f3f4f6',
    sidebarBg: '#ffffff',
    accent: '#1f2937', // slate-800 (Create New button & active sidebar)
    border: '#d1d5db',
    text: '#0f1724',
    muted: '#6b7280',
    iconColor: '#1f2937',
    hoverBg: '#eff6ff',
    logout: '#1f2937' // slate-800 for logout/profile requested
  }), []);

  // Inject CSS variables and scoped stylesheet so Sidebar matches pageTheme
  useEffect(() => {
    // guard for SSR
    if (typeof document === 'undefined') return;

    const themeVars = {
      '--sidebar-line-color': pageTheme.border,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--sidebar-text': pageTheme.text,
      '--accent-color': pageTheme.accent,
      '--accent-hover': pageTheme.accent,
      '--page-border-color': pageTheme.border,
      '--page-bg': pageTheme.bg,
      '--text-primary': pageTheme.text,
      '--text-muted': pageTheme.muted,
      '--icon-color': pageTheme.iconColor,
      '--hover-bg': pageTheme.hoverBg,
      '--sidebar-active-text': pageTheme.accent,
      '--sidebar-profile-bg': pageTheme.logout,
      '--logout-bg': pageTheme.logout
    };

    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = 'production-sidebar-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      /* Sidebar base: white background, black text for non-active items */
      .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
      .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item,
      .sidebar .nav-link, .app-sidebar .nav-link { color: var(--sidebar-text) !important; background-color: #ffffff !important; }

      /* Active item: use accent (slate-800) background with white text & icons */
      .sidebar a.active, .sidebar .nav-link.active, .sidebar .menu-item.active, .sidebar .nav-item.active,
      .app-sidebar a.active, .app-sidebar .nav-link.active {
        background-color: var(--accent-color) !important; /* slate-800 background */
        color: #ffffff !important; /* active text white and visible */
        font-weight: 700;
      }

      /* Force all nested text inside the active item to be white (covers many markup variants) */
      .sidebar a.active *,
      .sidebar .menu-item.active *,
      .sidebar .nav-link.active *,
      .app-sidebar a.active *,
      .app-sidebar .menu-item.active *,
      .app-sidebar .nav-link.active * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Specific common label selectors some sidebars use */
      .sidebar a.active .menu-text,
      .sidebar a.active .menu-item-text,
      .sidebar a.active .item-label,
      .sidebar .menu-item.active .menu-text,
      .sidebar .menu-item.active .menu-item-text,
      .sidebar .menu-item.active .item-label {
        color: #ffffff !important;
        opacity: 1 !important;
      }

      /* Ensure active icons also use white so they are visible on slate background */
      .sidebar a.active svg, .sidebar .nav-link.active svg, .app-sidebar a.active svg {
        color: #ffffff !important;
        fill: #ffffff !important;
      }

      /* If sidebar implementation used an inset bar, keep it subtle or remove */
      .sidebar a.active::before, .app-sidebar a.active::before {
        background: transparent !important;
      }


     

      /* Make sure hover still looks okay */
      .sidebar a:hover, .app-sidebar a:hover { background-color: rgba(0,0,0,0.03) !important; }
    `;

    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [pageTheme]);
  // --- end theme hooks ---

  useEffect(() => {
    fetchProductionOrders();
    fetchFormData();
    console.log("ProductionOrderPage component is rendering");
  }, [refreshKey]);
  
  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
   const handleLogout = () => {
    navigate('/login');
  };

  const fetchProductionOrders = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await authenticatedFetch(`${API_URL}/production-order`);
      
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Authentication required. Please log in again.");
        }
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const data = await res.json();
      console.log("Production Orders API Response:", data);
      
      setProductionOrders(data || []);
    } catch (error) {
      console.error("Error fetching Production Orders:", error);
      setApiError(error.message);
      setProductionOrders([]);
    }
    setLoading(false);
  };
  
  const fetchFormData = async () => {
    try {
      const res = await authenticatedFetch(`${API_URL}/production-order/form-data`);
      
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const data = await res.json();
      console.log("Form Data Response:", data);
      
      setFormData({
        products: data.products || [],
        nextOrderNumber: data.nextOrderNumber || '',
        nextDetailNumber: data.nextDetailNumber || ''
      });
    } catch (error) {
      console.error("Error fetching form data:", error);
      setApiError(error.message);
    }
  };
  
  const fetchOrderDetails = async (orderId) => {
    try {
      const res = await authenticatedFetch(`${API_URL}/production-order/${orderId}`);
      
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const data = await res.json();
      console.log("Production Order details response:", data);
      
      return data;
    } catch (error) {
      console.error("Error fetching Production Order details:", error);
      throw error;
    }
  };
  
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    
    if (!Array.isArray(productionOrders)) return;
    
    const sortedOrders = [...productionOrders].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === "CreatedAt" || key === "UpdatedAt") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === "Qty") {
        const aNum = parseFloat(a[key]) || 0;
        const bNum = parseFloat(b[key]) || 0;
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      
      // Handle string fields
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setProductionOrders(sortedOrders);
  };
  
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ml-1 w-3 h-3" />
    ) : (
      <FaSortDown className="ml-1 w-3 h-3" />
    );
  };
  
  const handleView = async (order) => {
    try {
      const completeOrder = await fetchOrderDetails(order.ProductionOrderID);
      setSelectedOrder(completeOrder);
      setShowView(true);
    } catch (error) {
      console.error("Error fetching Production Order details for view:", error);
      alert("Error loading Production Order details. Please try again.");
    }
  };
  
  const handleEdit = async (order) => {
    try {
      const completeOrder = await fetchOrderDetails(order.ProductionOrderID);
      setSelectedOrder(completeOrder);
      setIsEdit(true);
      setShowForm(true);
    } catch (error) {
      console.error("Error fetching Production Order details for edit:", error);
      alert("Error loading Production Order details for editing. Please try again.");
    }
  };
  
  const handleDelete = async (order) => {
    if (!window.confirm("Are you sure you want to delete this Production Order?"))
      return;
    try {
      const response = await authenticatedFetch(
        `${API_URL}/production-order/${order.ProductionOrderID}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete Production Order");
      }
      fetchProductionOrders();
    } catch (error) {
      console.error("Error deleting Production Order:", error);
      alert(
        error.message || "An error occurred while deleting the Production Order"
      );
    }
  };
  
  const handleDownloadCSV = () => {
    if (!Array.isArray(productionOrders) || productionOrders.length === 0) {
      alert("No Production Order data to export");
      return;
    }
    
    const headers = [
      "Order ID",
      "BOM Type",
      "Product Code",
      "Product Name",
      "Quantity",
      "UOM",
      "Status",
      "Created By",
      "Created At",
      "Updated By",
      "Updated At",
    ];
    
    const rows = productionOrders.map((order) => [
      order.ProductionOrderID || "",
      order.BOMType || "",
      order.ProductCode || "",
      order.ProductName || "",
      order.Qty || "",
      order.UOM || "",
      order.Status || "",
      order.CreatedBy || "",
      order.CreatedAt ? dayjs(order.CreatedAt).format("YYYY-MM-DD HH:mm") : "",
      order.UpdatedBy || "",
      order.UpdatedAt ? dayjs(order.UpdatedAt).format("YYYY-MM-DD HH:mm") : "",
    ]);
    
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "production-orders.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const handleCreate = () => {
    setSelectedOrder(null);
    setIsEdit(false);
    setShowForm(true);
  };
  
  const handleFormSubmit = async (response) => {
    setFormLoading(true);
    try {
      // Show success message
      alert(response.message || 'Production Order saved successfully');
      
      // Close the form modal
      setShowForm(false);
      
      // Refresh the Production Order list
      await fetchProductionOrders();
      
      // Force re-render of modals by updating refreshKey
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Error saving Production Order:", error);
      alert(error.message || "An error occurred while saving the Production Order");
    } finally {
      setFormLoading(false);
    }
  };
  
  const getStatusColor = (status) => {
    const isActive = status === "Active" || status === 1;
    return isActive ? "bg-[#2ecc71] text-white" : "bg-[#e74c3c] text-white";
  };
  
  const getStatusText = (status) => {
    if (status === true || status === 1 || status === "1") return "Active";
    if (status === false || status === 0 || status === "0") return "Inactive";
    return status || "Unknown";
  };
  
  // Filter Production Orders based on search term and filters
  const filteredOrders = Array.isArray(productionOrders) ? productionOrders.filter((order) => {
    if (!order || typeof order !== 'object') return false;
    
    const matchesSearch =
      searchTerm === "" ||
      (order.ProductionOrderID && order.ProductionOrderID.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.ProductName && order.ProductName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.BOMType && order.BOMType.toLowerCase().includes(searchTerm.toLowerCase()));
      
    const orderStatus = getStatusText(order.Status);
    const matchesStatus =
      status === "All" ||
      (status === "Active" && orderStatus === "Active") ||
      (status === "Inactive" && orderStatus === "Inactive");
      
    return matchesSearch && matchesStatus;
  }) : [];
  
  // Pagination logic
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const ordersForPage = filteredOrders.slice(startIndex, endIndex);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    pages.push(
      <button
        key="prev"
        className="p-1 rounded-md hover:bg-gray-200"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <LeftArrowIcon className="w-3 h-3" />
      </button>
    );
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`px-4 py-1  transition-colors ${
            currentPage === i
              ? "bg-slate-800 text-white font-semibold"
              : "hover:bg-gray-200"
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    pages.push(
      <button
        key="next"
        className="p-1 rounded-md hover:bg-gray-200"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <RightArrowIcon className="w-3 h-3" />
      </button>
    );
    
    return pages;
  };
  
  // Calculate stats with safety checks
  const totalOrders = Array.isArray(productionOrders) ? productionOrders.length : 0;
  const activeOrders = Array.isArray(productionOrders) 
    ? productionOrders.filter(o => getStatusText(o.Status) === "Active").length 
    : 0;
  const inactiveOrders = Array.isArray(productionOrders) 
    ? productionOrders.filter(o => getStatusText(o.Status) === "Inactive").length 
    : 0;
  const thisMonthOrders = Array.isArray(productionOrders) 
    ? productionOrders.filter(o => {
        const createdDate = new Date(o.CreatedAt);
        const now = new Date();
        return (
          createdDate.getMonth() === now.getMonth() &&
          createdDate.getFullYear() === now.getFullYear()
        );
      }).length 
    : 0;
  
  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('PRODUCTION_ORDER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('PRODUCTION_ORDER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('PRODUCTION_ORDER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('PRODUCTION_ORDER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex" style={{ backgroundColor: pageTheme.bg, '--page-border-color': pageTheme.border }}>
      <Sidebar collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logout, logoutBg: pageTheme.logout }} 
        onLogout={handleLogout}/>
      <main className={`flex-1 p-8 overflow-hidden ml-20 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
     
        {/* Header Section */}
        <div className="mb-2">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-black mb-2">
                Production Orders
              </h1>
              <p className="text-gray-600">
                Manage your production orders
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="px-4 py-2  bg-white text-slate-800 hover:bg-slate-700 transition-all duration-300 flex items-center gap-2"
                onClick={handleDownloadCSV}
              >
                <FaDownload className="w-4 h-4" />
                Export
              </button>
              {canCreate && (
                <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm bg-slate-800 text-white" style={{  color: '#ffffff' }} >
                                <FaPlus className="h-4 w-4" />
                                Create New
                              </button>
              )}
            </div>
          </div>
        </div>
        
        {/* API Error Display */}
        {apiError && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p>Error loading Production Orders: {apiError}</p>
            <div className="mt-2 flex gap-2">
              <button 
                onClick={fetchProductionOrders}
                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Retry
              </button>
              {apiError.includes("Authentication required") && (
                <button 
                  onClick={() => {
                    window.location.href = '/login';
                  }}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        )}

        {/* full-width divider above stats */}
<div className="w-full border-t border-gray-300 my-6" />

        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Total Orders
                </p>
                <p className="text-2xl font-bold text-black">
                  {totalOrders}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-blue-100 text-blue-600">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Active Orders
                </p>
                <p className="text-2xl font-bold text-black">
                  {activeOrders}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-green-100 text-green-600">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Inactive Orders
                </p>
                <p className="text-2xl font-bold text-black]">
                  {inactiveOrders}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-red-100 text-red-600">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  This Month
                </p>
                <p className="text-2xl font-bold text-black">
                  {thisMonthOrders}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-purple-100 text-purple-600">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        

        
        {/* Search and Filters */}
        {/* ---------- Search & Filter (Sales Order visual parity) ---------- */}
<section className="p-5 bg-white border border-gray-300 shadow-md mb-6">
  <div className="flex flex-col md:flex-row gap-3 items-end">
    <div className="flex-1">
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        What are you looking for?
      </label>
      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" aria-hidden="true" />
        <input
          type="text"
          placeholder="Search for Order ID, product, type, etc"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        Status
      </label>
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        className="px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
      >
        <option value="All">All</option>
        <option value="Active">Active</option>
        <option value="Inactive">Inactive</option>
      </select>
    </div>

    <button
      onClick={() => { setCurrentPage(1); }}
      className="px-5 py-2 bg-slate-800 text-white font-medium hover:bg-slate-700 transition-all duration-200 shadow-sm"
    >
      Search
    </button>
  </div>
</section>

        
        {/* ---------- Production Orders Table (Sales Order visual parity) ---------- */}
        <section className="bg-white border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300">
            <h2 className="text-xl font-semibold text-gray-900">Production Orders Summary</h2>
            <div className="flex gap-2">
              <div className="flex items-center space-x-1 text-gray-500">
                {renderPaginationButtons && typeof renderPaginationButtons === 'function' ? renderPaginationButtons() : null}
              </div>
            </div>
          </div>
          

          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading Production Orders...</div>
          ) : ordersForPage.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {apiError ? "Failed to load Production Orders" : "No Production Orders found"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-200 text-gray-600 text-xs font-semibold uppercase">
                  <tr>
                    {[
                      { key: "ProductionOrderID", label: "Order ID" },
                      { key: "ProductName", label: "Product" },
                      { key: "BOMType", label: "BOM Type" },
                      { key: "Qty", label: "Quantity" },
                      { key: "Status", label: "Status" },
                      { key: "CreatedAt", label: "Created Date" },
                      { key: "UpdatedAt", label: "Last Updated" },
                      { key: "actions", label: "Actions" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200"
                        onClick={() => col.key !== "actions" && handleSort(col.key)}
                      >
                        <div className="flex items-center">
                          {col.label}
                          {col.key !== "actions" && getSortIcon && typeof getSortIcon === 'function' ? (
                            <span className="ml-2">{getSortIcon(col.key)}</span>
                          ) : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-300">
                  {ordersForPage.map((order, idx) => (
                    <tr
                      key={order.ProductionOrderID || idx}
                      className="hover:bg-gray-50 transition-colors duration-200 text-balck"
                    >
                      <td className="px-6 py-3 font-medium text-gray-900">
                        <p
                          className={`cursor-pointer ${!canView ? "pointer-events-none text-gray-600" : "text-black hover:underline"}`}
                          onClick={() => canView && handleView(order)}
                        >
                          {order.ProductionOrderID || "N/A"}
                        </p>
                      </td>

                      <td className="px-6 py-3 text-gray-600">{order.ProductName || "N/A"}</td>

                      <td className="px-6 py-3 text-gray-600">{order.BOMType || "N/A"}</td>

                      <td className="px-6 py-3 text-gray-600">
                        {order.Qty ? parseFloat(order.Qty).toFixed(2) : "N/A"}
                      </td>

                      <td className="px-6 py-3">
                        <span
                          className={`inline-block px-3 py-1 text-xs font-medium rounded-full transition-colors duration-200 ${
                            (order.Status === true || order.Status === 1 || order.Status === "Active" || order.Status === "1")
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                          }`}
                        >
                          {getStatusText ? getStatusText(order.Status) : (order.Status || "N/A")}
                        </span>
                      </td>

                      <td className="px-6 py-3 text-gray-600">
                        {order.CreatedAt ? dayjs(order.CreatedAt).format("DD MMM YYYY") : "N/A"}
                      </td>

                      <td className="px-6 py-3 text-gray-600">
                        {order.UpdatedAt ? (
                          <div className="flex flex-col leading-tight">
                            <span>{dayjs(order.UpdatedAt).format("DD MMM YYYY")}</span>
                            <span className="text-xs text-gray-500">{dayjs(order.UpdatedAt).format("HH:mm")}</span>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>

                      <td className="px-6 py-3">
                        <div className="flex gap-3 justify-center">
                          {canView && (
                            <button
                              onClick={() => handleView(order)}
                              title="View"
                              className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            >
                              <FaEye className="h-4 w-4" />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => handleEdit(order)}
                              title="Edit"
                              className="text-green-600 hover:text-green-800 transition-colors duration-200"
                            >
                              <FaEdit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(order)}
                              title="Delete"
                              className="text-red-600 hover:text-red-800 transition-colors duration-200"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDownloadCSV()}
                            title="Download CSV"
                            className="text-purple-600 hover:text-purple-800 transition-colors duration-200"
                          >
                            <FaDownload className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Modals */}
        <ProductionOrderFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedOrder}
          isEdit={isEdit}
          loading={formLoading}
          formData={formData}
          key={`form-${refreshKey}`} // Add key to force re-render
        />
        <ProductionOrderViewModal
          open={showView}
          onClose={() => setShowView(false)}
          order={selectedOrder}
          key={`view-${refreshKey}`} // Add key to force re-render
        />
      </main>
    </div>
  );
};

export default ProductionOrderMaster;
